/**
 * Decides which decrypted records can be trusted as a device's own.
 *
 * Every record an up-to-date agent sends is signed with the device's key,
 * inside the encryption, and names the device (and, for a sealed day, the
 * day). A record whose signature fails, or that names another device or day,
 * is rejected: it was forged, altered or moved by whoever stored it. An
 * unsigned record is accepted only if it was stored before the device had a
 * signing key (an agent older than signing); after that, an unsigned record
 * can only have been inserted by someone else.
 *
 * The device's key comes from the Hub, which can never change it once set.
 * Whether the organization vouched for that key is `endorsed`: the identity's
 * signature over it, checked here, so a key the Hub made up is visible.
 */
import { base64ToBytes } from '~/util/bytes';
import { normalizeActivityPayload } from '~/util/intervalPayload';
import { decryptOrgSummary, DecryptionError, gunzip } from '~/util/orgKeypair';
import { endorsementMessage, openRecord, RECORD_MAGIC, verifySignature } from '~/util/orgSigning';
/** Unsigned records stored this long after a device registered its key are
 * still accepted: uploads already in flight when the agent upgraded. */
const UNSIGNED_GRACE_SECONDS = 10 * 60;
/** What to trust for `device`, checking its endorsement against `identity`
 * (the organization identity's public key, base64) when there is one. */
export function deviceTrust(device, organizationId, identity) {
    const signingKey = device.signing_public_key ?? null;
    const endorsed = !!signingKey &&
        !!identity &&
        !!device.endorsement &&
        verifySignature(identity, endorsementMessage(organizationId, device.id, signingKey), device.endorsement);
    return {
        identityKnown: !!identity,
        deviceId: device.id,
        signingKey,
        registeredAt: device.signing_key_registered_at
            ? Math.floor(Date.parse(device.signing_key_registered_at) / 1000)
            : null,
        endorsed,
    };
}
/** A record that can't be trusted as the device's own. */
export class RejectedRecord extends Error {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'RejectedRecord'
        });
    }
}
/** A signed record from a computer the client hasn't verified yet (one
 * approved from the pre-approved list, say): left out like a rejected one,
 * but nothing says it was tampered with. */
export class NotYetVerified extends RejectedRecord {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'NotYetVerified'
        });
    }
}
function receivedUnix(receivedAt) {
    return Math.floor(Date.parse(receivedAt) / 1000);
}
function parseJson(bytes) {
    try {
        const value = JSON.parse(new TextDecoder().decode(bytes));
        if (value && typeof value === 'object')
            return value;
    }
    catch {
        // Falls through.
    }
    throw new DecryptionError('Decrypted successfully, but the payload was not valid JSON.');
}
function acceptUnsigned(trust, receivedAt, payload) {
    if (trust.registeredAt === null)
        return;
    if (receivedAt > trust.registeredAt + UNSIGNED_GRACE_SECONDS) {
        throw new RejectedRecord('an unsigned record from after the device began signing');
    }
    // The Hub says when it received a record, so that alone can be backdated:
    // the record's own times must be from before signing began as well. (An
    // unsigned record can't be told from one the Hub wrote, so history from
    // before a device signed is only as good as the Hub's word; see the
    // solution document.)
    const end = Math.max(payload.window_end, ...payload.events.map(e => (e.start ?? payload.window_start) + e.duration_secs));
    if (Number.isFinite(end) && end > trust.registeredAt + UNSIGNED_GRACE_SECONDS) {
        throw new RejectedRecord('an unsigned record covering time after the device began signing');
    }
}
/** Checks one record's bytes. `day`, for a sealed day, is the day it must name. */
export function verifyRecordBytes(bytes, trust, receivedAt, day = null) {
    const opened = openRecord(bytes, trust.signingKey);
    if (opened.status === 'invalid' || !opened.body) {
        throw new RejectedRecord("a signature that doesn't verify");
    }
    const value = parseJson(opened.body);
    const payload = normalizeActivityPayload(value);
    if (opened.status === 'unsigned') {
        acceptUnsigned(trust, receivedAt, payload);
    }
    else {
        // A record naming another device or day is tampering whoever signed
        // it; only a record that is otherwise right is "not verified yet".
        if (value.device_id !== trust.deviceId) {
            throw new RejectedRecord('signed by this device, but naming another');
        }
        if (day !== null && value.day !== day) {
            throw new RejectedRecord('signed for another day');
        }
        if (trust.identityKnown && !trust.endorsed) {
            throw new NotYetVerified('signed by a key the client has not verified yet');
        }
    }
    return { payload, status: opened.status, plaintext: bytes };
}
/** Decrypts and checks one summary. */
export async function decryptVerifiedSummary(secret, record, trust) {
    const bytes = await decryptOrgSummary(secret, record);
    return verifyRecordBytes(bytes, trust, receivedUnix(record.received_at));
}
function startsWithMagic(bytes) {
    return RECORD_MAGIC.every((b, i) => bytes[i] === b);
}
/**
 * Decrypts and checks one sealed day. An agent's seal is one signed record
 * naming the day. A browser's seal (`sealed_records`) holds the device's
 * original signed records, each checked on its own; it may not carry
 * anything unsigned. A day from an agent older than signing is unsigned.
 */
export async function decryptVerifiedDay(secret, bundle, trust) {
    const sealed = await decryptOrgSummary(secret, bundle);
    let bytes;
    try {
        bytes = await gunzip(sealed);
    }
    catch {
        throw new DecryptionError('Decrypted successfully, but the sealed day was not valid gzip.');
    }
    const receivedAt = receivedUnix(bundle.received_at);
    if (startsWithMagic(bytes))
        return [verifyRecordBytes(bytes, trust, receivedAt, bundle.day)];
    const value = parseJson(bytes);
    if (value.kind === 'sealed_records') {
        if (value.device_id !== trust.deviceId || value.day !== bundle.day) {
            throw new RejectedRecord('a sealed day naming another device or day');
        }
        const records = Array.isArray(value.records) ? value.records : [];
        return records.map(encoded => {
            const verified = verifyRecordBytes(base64ToBytes(encoded), trust, receivedAt);
            if (verified.status !== 'signed') {
                throw new RejectedRecord('an unsigned record inside a sealed day');
            }
            return verified;
        });
    }
    const payload = normalizeActivityPayload(value);
    acceptUnsigned(trust, receivedAt, payload);
    return [{ payload, status: 'unsigned', plaintext: bytes }];
}
